import { useReconciliationServiceIntegrationSetting } from '@/context/services/invoice/ReceivedInvoice.service';
import { useReconciliationServiceIntegrationBeta2Flag } from '@/hooks/feature_flag/useReconciliationServiceIntegrationBeta2Flag';
import { createContext, ReactNode, useContext, useRef } from 'react';

const ReconciliationOfficeSettingContext = createContext<boolean | null>(null);

/**
 * Settings changed in another session must not flip search fields/columns
 * visible or hidden mid-session (NA-5933) — so we pin the first resolved
 * value for the lifetime of this provider and ignore later changes. A full
 * page reload remounts the provider and picks up the current value.
 *
 * Children are not rendered until the value resolves: the search form and
 * query are built from the URL on first render, and building them with a
 * provisional `false` drops `reconciliation_status_in` — the page clamp in
 * the list then writes that filter-less query back to the URL.
 */
export const ReconciliationOfficeSettingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const isEnabledFromResponse = useReconciliationServiceIntegrationSetting();

  const pinned = useRef<boolean | null>(null);
  if (pinned.current === null && typeof isEnabledFromResponse === 'boolean') {
    pinned.current = isEnabledFromResponse;
  }

  if (pinned.current === null) {
    return <></>;
  }

  return (
    <ReconciliationOfficeSettingContext.Provider value={pinned.current}>
      {children}
    </ReconciliationOfficeSettingContext.Provider>
  );
};

const useReconciliationOfficeSettingEnabled = (): boolean => {
  const context = useContext(ReconciliationOfficeSettingContext);
  if (context === null) {
    throw new Error(
      'useReconciliationStatusVisible must be used within ReconciliationOfficeSettingProvider'
    );
  }
  return context;
};

/**
 * 突合サービス連携 is shown only when both gates are open: the feature flag is the
 * FE-side rollout kill-switch, `is_enabled` is the office's own setting. Keeping
 * both checks in one place means turning the flag off hides the feature
 * everywhere.
 *
 * TODO(post-rollout cleanup): once fully rolled out, drop `isRolledOut` and the
 * `useReconciliationServiceIntegrationBeta2Flag` import below, and return
 * `isEnabledForOffice` directly. No change needed to the Provider/Context above.
 */
export const useReconciliationStatusVisible = (): boolean => {
  const isRolledOut = useReconciliationServiceIntegrationBeta2Flag();
  const isEnabledForOffice = useReconciliationOfficeSettingEnabled();

  return isRolledOut && isEnabledForOffice;
};
